/**
 * QuizCatcher — falling-token MCQ.
 * Prompt at the top; tokens drift down; player moves a catcher left/right
 * (or drags) to catch the correct answer. Wrong catch = -1 life.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import type { QuizGameSpec } from "@/types/quizGameSpec";
import type { GameRunResult } from "@/types/gameSpec";
import { GameHud } from "./hud/GameHud";
import { attemptsToRunResult, shuffle, type ArchetypeAttempt } from "./shared";

interface Props {
  spec: QuizGameSpec;
  onComplete: (r: GameRunResult) => void;
  onExit?: () => void;
}

interface Token {
  id: number;
  text: string;
  isCorrect: boolean;
  x: number; // 0..1
  y: number; // px
  vy: number;
}

const FIELD_H = 420;
const FIELD_W_DEFAULT = 640;
const CATCHER_W = 96;

export const QuizCatcher = ({ spec, onComplete, onExit }: Props) => {
  const { tuning, questions, theme } = spec;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const attemptsRef = useRef<ArchetypeAttempt[]>([]);
  const startedAt = useRef(Date.now());
  const questionStartedAt = useRef(Date.now());
  const tokenIdRef = useRef(0);

  const [qIdx, setQIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(tuning.lives);
  const [timeLeft, setTimeLeft] = useState(tuning.timer_s);
  const [tokens, setTokens] = useState<Token[]>([]);
  const [catcherX, setCatcherX] = useState(0.5);
  const [fieldW, setFieldW] = useState(FIELD_W_DEFAULT);
  const [feedback, setFeedback] = useState<null | { kind: "good" | "bad"; text: string }>(null);
  const [streak, setStreak] = useState(0);
  const [streakMax, setStreakMax] = useState(0);
  const [finished, setFinished] = useState(false);

  const question = questions[qIdx];
  const maxScore = questions.length;
  const totalQuestions = questions.length;

  // Field width tracking
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((e) => setFieldW(e[0].contentRect.width));
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Overall timer
  useEffect(() => {
    if (finished) return;
    const id = window.setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          window.clearInterval(id);
          finish(false);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  // Spawn tokens for current question
  useEffect(() => {
    if (finished || !question) return;
    tokenIdRef.current = 0;
    setTokens([]);
    const pool = shuffle([
      { text: question.correct, isCorrect: true },
      ...(question.distractors ?? []).slice(0, 3).map((d) => ({ text: d, isCorrect: false })),
    ]);
    let cancelled = false;
    (async () => {
      for (const item of pool) {
        if (cancelled || finished) return;
        setTokens((prev) => [
          ...prev,
          {
            id: ++tokenIdRef.current,
            text: item.text,
            isCorrect: item.isCorrect,
            x: 0.1 + Math.random() * 0.8,
            y: -40,
            vy: tuning.fall_speed,
          },
        ]);
        await new Promise((r) => setTimeout(r, 1000 / Math.max(0.3, tuning.spawn_rate)));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qIdx, question?.prompt, finished]);

  // Animation loop
  useEffect(() => {
    if (finished) return;
    let last = performance.now();
    const step = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      setTokens((prev) => {
        const catcherLeft = catcherX * fieldW - CATCHER_W / 2;
        const catcherRight = catcherLeft + CATCHER_W;
        const catcherY = FIELD_H - 40;
        const kept: Token[] = [];
        for (const t of prev) {
          const ny = t.y + t.vy * dt;
          const tx = t.x * fieldW;
          const caught = ny >= catcherY && ny <= catcherY + 40 && tx >= catcherLeft && tx <= catcherRight;
          if (caught) {
            handleCatch(t.isCorrect, t.text);
            continue;
          }
          if (ny > FIELD_H + 40) {
            if (t.isCorrect) handleMiss(t.text);
            continue;
          }
          kept.push({ ...t, y: ny });
        }
        return kept;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catcherX, fieldW, finished]);

  // Keyboard + pointer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "a") setCatcherX((x) => Math.max(0, x - 0.06));
      if (e.key === "ArrowRight" || e.key === "d") setCatcherX((x) => Math.min(1, x + 0.06));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onPointerMove = (e: React.PointerEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setCatcherX(x);
  };

  function recordAttempt(isCorrect: boolean, answered: string) {
    attemptsRef.current.push({
      prompt: question.prompt,
      correct: question.correct,
      answered,
      isCorrect,
      timeMs: Date.now() - questionStartedAt.current,
    });
  }

  function nextQuestion(delayMs = 700) {
    setTimeout(() => {
      if (finished) return;
      if (qIdx + 1 >= totalQuestions) {
        finish(true);
      } else {
        setQIdx((i) => i + 1);
        questionStartedAt.current = Date.now();
        setFeedback(null);
      }
    }, delayMs);
  }

  function handleCatch(isCorrect: boolean, text: string) {
    if (feedback) return; // already resolving
    recordAttempt(isCorrect, text);
    if (isCorrect) {
      setScore((s) => s + 1);
      setStreak((s) => {
        const n = s + 1;
        setStreakMax((m) => Math.max(m, n));
        return n;
      });
      setFeedback({ kind: "good", text: "Correct!" });
    } else {
      setLives((l) => {
        const n = l - 1;
        if (n <= 0) queueMicrotask(() => finish(false));
        return n;
      });
      setStreak(0);
      setFeedback({ kind: "bad", text: `Answer: ${question.correct}` });
    }
    setTokens([]);
    nextQuestion();
  }

  function handleMiss(_text: string) {
    if (feedback) return;
    recordAttempt(false, "(missed)");
    setLives((l) => {
      const n = l - 1;
      if (n <= 0) queueMicrotask(() => finish(false));
      return n;
    });
    setStreak(0);
    setFeedback({ kind: "bad", text: `Answer: ${question.correct}` });
    setTokens([]);
    nextQuestion();
  }

  function finish(won: boolean) {
    if (finished) return;
    setFinished(true);
    const result = attemptsToRunResult({
      archetype: "quiz-catcher",
      attempts: attemptsRef.current,
      startedAt: startedAt.current,
      won: won && lives > 0,
      score,
      maxScore,
      streakMax,
    });
    onComplete(result);
  }

  const progress = Math.round((qIdx / totalQuestions) * 100);

  return (
    <div className="space-y-3">
      <GameHud
        title={theme.title}
        score={score}
        maxScore={maxScore}
        lives={lives}
        timeLeft={timeLeft}
        progress={progress}
        streak={streak}
      />

      <div
        className="rounded-lg overflow-hidden select-none touch-none relative"
        style={{ background: theme.palette.bg, height: FIELD_H, color: "#fff" }}
        ref={containerRef}
        onPointerMove={onPointerMove}
      >
        {/* Prompt */}
        <div
          className="absolute top-0 left-0 right-0 px-4 py-3 text-center font-semibold"
          style={{ background: theme.palette.surface }}
        >
          {question?.prompt}
        </div>

        {/* Tokens */}
        <AnimatePresence>
          {tokens.map((t) => (
            <motion.div
              key={t.id}
              className="absolute px-3 py-2 rounded-md text-sm font-medium shadow-md"
              style={{
                left: `calc(${t.x * 100}% - 40px)`,
                top: t.y,
                background: theme.palette.surface,
                border: `2px solid ${theme.palette.accent}`,
                minWidth: 80,
                textAlign: "center",
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
            >
              {t.text}
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Catcher */}
        <div
          className="absolute rounded-md"
          style={{
            left: `calc(${catcherX * 100}% - ${CATCHER_W / 2}px)`,
            top: FIELD_H - 40,
            width: CATCHER_W,
            height: 24,
            background: theme.palette.accent,
            boxShadow: "0 6px 16px rgba(0,0,0,0.3)",
          }}
        />

        {/* Feedback */}
        <AnimatePresence>
          {feedback && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="absolute inset-x-0 bottom-16 text-center text-lg font-bold"
              style={{ color: feedback.kind === "good" ? theme.palette.good : theme.palette.bad }}
            >
              {feedback.text}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="text-xs text-muted-foreground text-center">
        Move with your mouse, touch, or ← → keys. Catch the correct answer.
      </div>

      {onExit && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={onExit}>Exit game</Button>
        </div>
      )}
    </div>
  );
};

export default QuizCatcher;
